import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { instituteService } from '../../../services/instituteService';
import { supabase } from '../../../lib/supabase';

const ALL_CATEGORIES = [
  { id: 'Engineering', label: 'Engineering', icon: '⚙️', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  { id: 'Management', label: 'Management', icon: '📊', color: 'bg-purple-100 text-purple-700 border-purple-200' },
  { id: 'Law', label: 'Law', icon: '⚖️', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  { id: 'Medical', label: 'Medical', icon: '🏥', color: 'bg-red-100 text-red-700 border-red-200' },
  { id: 'Arts & Science', label: 'Arts & Science', icon: '🎨', color: 'bg-pink-100 text-pink-700 border-pink-200' },
  { id: 'Architecture', label: 'Architecture', icon: '🏛️', color: 'bg-orange-100 text-orange-700 border-orange-200' },
  { id: 'Pharmacy', label: 'Pharmacy', icon: '💊', color: 'bg-green-100 text-green-700 border-green-200' },
  { id: 'Agriculture', label: 'Agriculture', icon: '🌾', color: 'bg-lime-100 text-lime-700 border-lime-200' },
  { id: 'Design', label: 'Design', icon: '✏️', color: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
  { id: 'Hotel Management', label: 'Hotel Management', icon: '🏨', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  { id: 'Education', label: 'Education', icon: '📚', color: 'bg-teal-100 text-teal-700 border-teal-200' },
  { id: 'Nursing', label: 'Nursing', icon: '🩺', color: 'bg-cyan-100 text-cyan-700 border-cyan-200' },
];

const CategoryEditor = () => {
  const [institutes, setInstitutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [editingId, setEditingId] = useState(null);
  const [editingCategories, setEditingCategories] = useState([]);
  const [saving, setSaving] = useState(null);
  const [successId, setSuccessId] = useState(null);

  useEffect(() => {
    loadInstitutes();
  }, []);

  const loadInstitutes = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await instituteService?.getAllInstitutesForAdmin({ approvalStatus: 'approved' });
      setInstitutes(data || []);
    } catch (err) {
      setError(err?.message || 'Failed to load institutes');
    } finally {
      setLoading(false);
    }
  };

  const handleEditOpen = (institute) => {
    setEditingId(institute?.id);
    setEditingCategories([...(institute?.categories || [])]);
  };

  const handleEditCancel = () => {
    setEditingId(null);
    setEditingCategories([]);
  };

  const handleToggleCategory = (categoryId) => {
    setEditingCategories(prev =>
      prev?.includes(categoryId)
        ? prev?.filter(c => c !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleSave = async (instituteId) => {
    try {
      setSaving(instituteId);
      const { error } = await supabase?.from('institute_profiles')?.update({ categories: editingCategories })?.eq('id', instituteId);

      if (error) throw error;

      setInstitutes(prev =>
        prev?.map(i =>
          i?.id === instituteId ? { ...i, categories: [...editingCategories] } : i
        )
      );
      setEditingId(null);
      setEditingCategories([]);
      setSuccessId(instituteId);
      setTimeout(() => setSuccessId(null), 2000);
    } catch (err) {
      alert(err?.message || 'Failed to save categories');
    } finally {
      setSaving(null);
    }
  };

  const filtered = institutes?.filter(i => {
    const matchesSearch = i?.instituteName?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesFilter =
      filterCategory === 'all' ||
      i?.categories?.includes(filterCategory);
    return matchesSearch && matchesFilter;
  });

  const getCategoryObj = (id) => ALL_CATEGORIES?.find(c => c?.id === id);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <p className="text-red-700 mb-3">{error}</p>
        <Button onClick={loadInstitutes} iconName="RefreshCw" iconPosition="left" size="sm">Retry</Button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-foreground">Category Editor</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Assign or edit categories for each institute from the <span className="font-semibold text-primary">12 available categories</span>.
          </p>
        </div>
        <div className="flex items-center gap-2 bg-primary/10 border border-primary/20 px-4 py-2 rounded-full">
          <Icon name="Tag" size={16} color="var(--color-primary)" />
          <span className="text-sm font-bold text-primary">{institutes?.length} Institutes</span>
        </div>
      </div>

      {/* Category Legend */}
      <div className="bg-gray-50 border border-border rounded-xl p-4 mb-6">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Available Categories</p>
        <div className="flex flex-wrap gap-2">
          {ALL_CATEGORIES?.map(cat => (
            <span
              key={cat?.id}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${cat?.color}`}
            >
              <span>{cat?.icon}</span>
              {cat?.label}
            </span>
          ))}
        </div>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Icon name="Search" size={16} color="var(--color-muted-foreground)" className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            type="text"
            placeholder="Search institutes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            className="w-full pl-9 pr-4 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
          />
        </div>
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e?.target?.value)}
          className="border border-border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-white"
        >
          <option value="all">All Categories</option>
          {ALL_CATEGORIES?.map(cat => (
            <option key={cat?.id} value={cat?.id}>{cat?.icon} {cat?.label}</option>
          ))}
        </select>
      </div>

      {/* Institute List */}
      <div className="space-y-3 max-h-[620px] overflow-y-auto pr-1">
        {filtered?.length === 0 ? (
          <div className="bg-card border border-border rounded-lg p-8 text-center">
            <Icon name="Search" size={40} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
            <p className="text-muted-foreground">No institutes found</p>
          </div>
        ) : (
          filtered?.map(institute => (
            <div
              key={institute?.id}
              className={`bg-white border rounded-xl p-4 transition-all ${
                editingId === institute?.id ? 'border-primary shadow-sm' : 'border-border hover:border-gray-300'
              } ${successId === institute?.id ? 'border-green-400 bg-green-50/30' : ''}`}
            >
              <div className="flex items-start gap-4">
                {/* Logo */}
                <div className="w-11 h-11 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                  <img
                    src={institute?.logoUrl || 'https://img.rocket.new/generatedImages/rocket_gen_img_1e7bb1524-1766485632262.png'}
                    alt={`${institute?.instituteName} logo`}
                    className="w-full h-full object-contain"
                  />
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <h3 className="font-semibold text-foreground text-sm">{institute?.instituteName}</h3>
                    {successId === institute?.id && (
                      <span className="inline-flex items-center gap-1 text-xs text-green-600 font-medium">
                        <Icon name="CheckCircle" size={13} color="#16a34a" /> Saved!
                      </span>
                    )}
                  </div>

                  {/* Current categories (view mode) */}
                  {editingId !== institute?.id && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {institute?.categories?.length > 0 ? (
                        institute?.categories?.map(catId => {
                          const cat = getCategoryObj(catId);
                          return cat ? (
                            <span
                              key={catId}
                              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${cat?.color}`}
                            >
                              {cat?.icon} {cat?.label}
                            </span>
                          ) : (
                            <span key={catId} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border bg-gray-100 text-gray-600 border-gray-200">
                              {catId}
                            </span>
                          );
                        })
                      ) : (
                        <span className="text-xs text-muted-foreground italic">No categories assigned</span>
                      )}
                    </div>
                  )}

                  {/* Edit mode: category checkboxes */}
                  {editingId === institute?.id && (
                    <div className="mt-3">
                      <p className="text-xs text-muted-foreground mb-2">Select all applicable categories:</p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                        {ALL_CATEGORIES?.map(cat => {
                          const isSelected = editingCategories?.includes(cat?.id);
                          return (
                            <button
                              key={cat?.id}
                              type="button"
                              onClick={() => handleToggleCategory(cat?.id)}
                              className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-all text-left ${
                                isSelected
                                  ? `${cat?.color} border-current shadow-sm`
                                  : 'bg-white border-border text-muted-foreground hover:border-gray-400 hover:text-foreground'
                              }`}
                            >
                              <span className="text-base leading-none">{cat?.icon}</span>
                              <span>{cat?.label}</span>
                              {isSelected && (
                                <Icon name="Check" size={11} className="ml-auto flex-shrink-0" />
                              )}
                            </button>
                          );
                        })}
                      </div>
                      <div className="flex items-center gap-2 mt-3">
                        <Button
                          size="sm"
                          onClick={() => handleSave(institute?.id)}
                          disabled={saving === institute?.id}
                          iconName={saving === institute?.id ? 'Loader2' : 'Save'}
                          iconPosition="left"
                        >
                          {saving === institute?.id ? 'Saving...' : `Save (${editingCategories?.length} selected)`}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleEditCancel}
                          disabled={saving === institute?.id}
                        >
                          Cancel
                        </Button>
                        {editingCategories?.length > 0 && (
                          <button
                            type="button"
                            onClick={() => setEditingCategories([])}
                            className="text-xs text-red-500 hover:text-red-700 ml-auto"
                          >
                            Clear all
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Edit button (view mode only) */}
                {editingId !== institute?.id && (
                  <button
                    onClick={() => handleEditOpen(institute)}
                    className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary border border-primary/30 rounded-lg hover:bg-primary/5 transition-colors"
                  >
                    <Icon name="Pencil" size={13} />
                    Edit
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default CategoryEditor;
